import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Visually-hidden ARIA live region for announcing transient, action-
 * driven feedback to screen readers (e.g. "Correct! Sodium.").
 *
 * Use the `useAnnouncer` hook to get an `announce(message, tone?)`
 * function and the `<LiveAnnouncer />` element to render once near the
 * top of the game tree.
 *
 * Tone semantics:
 * - "polite" (default): does not interrupt current speech. Best for
 *   answer feedback so timer ticks etc. aren't cut off.
 * - "assertive": interrupts. Reserve for urgent state changes (e.g.
 *   "Time's up!").
 *
 * The component appends a zero-width space when the same message is
 * announced twice in a row so screen readers re-read it (otherwise
 * identical consecutive text is suppressed).
 */
export type AnnounceTone = "polite" | "assertive";

type AnnounceFn = (message: string, tone?: AnnounceTone) => void;

const listeners = new Set<(msg: string, tone: AnnounceTone) => void>();

export function announce(message: string, tone: AnnounceTone = "polite") {
  for (const l of listeners) l(message, tone);
}

export function useAnnouncer(): AnnounceFn {
  return useCallback((message: string, tone: AnnounceTone = "polite") => {
    announce(message, tone);
  }, []);
}

export function LiveAnnouncer() {
  const [polite, setPolite] = useState("");
  const [assertive, setAssertive] = useState("");
  const lastPolite = useRef("");
  const lastAssertive = useRef("");

  useEffect(() => {
    const handler = (msg: string, tone: AnnounceTone) => {
      if (tone === "assertive") {
        const next = msg === lastAssertive.current ? msg + "\u200B" : msg;
        lastAssertive.current = next;
        setAssertive(next);
      } else {
        const next = msg === lastPolite.current ? msg + "\u200B" : msg;
        lastPolite.current = next;
        setPolite(next);
      }
    };
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
    };
  }, []);

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {polite}
      </div>
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {assertive}
      </div>
    </>
  );
}
